import { environment } from '../../../environments/environment';
import { Injectable } from '@angular/core';
import { CognitoCallback, CognitoUtil, LoggedInCallback } from './cognito.service';
import {
  AuthenticationDetails,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserSession
} from 'amazon-cognito-identity-js';
import * as AWS from 'aws-sdk/global';
import * as STS from 'aws-sdk/clients/sts';

import { Router } from '@angular/router';

// NGRX
import { Store } from '@ngrx/store';
import * as fromApp from '../store';
import * as appActions from '../store/actions/ui.actions';

import { BackendEndpointService } from './backend-endpoint.service';

export const enum LoginStatus {
  SUCCESS = 1,
  FAIL,
  CHANGE_PASSWORD
}

export interface PasswordPolicy {
  MinimumLength: Number;
  RequireLowercase: boolean;
  RequireNumbers: boolean;
  RequireSymbols: boolean;
  RequireUppercase: boolean;
}

@Injectable()
export class UserLoginService {
  public loading = false;
  public uiError = '';
  public redirectPath = '';

  private currentUserSession = null;
  private currentUserCredentials = null;
  private sessionRefresher = null;

  public validationRequired: {
    cognitoUser: CognitoUser;
    userAttributes?: any;
    requiredAttributes?: any;
  } = null;

  public loginCallback: CognitoCallback = {
    cognitoCallback: (message, result, value, error) => {
      this.uiError = '';

      switch (result) {
        case LoginStatus.SUCCESS:
          this.router.navigate(['/']);
          break;

        case LoginStatus.FAIL:
          if (error.code === 'PasswordResetRequiredException') {
            this.router.navigate(['/reset']);
          } else {
            this.uiError = message;
          }

          break;

        case LoginStatus.CHANGE_PASSWORD:
          this.router.navigate(['/validate']);
          break;

        default:
          break;
      }
    }
  };

  public redirectAfterLogin: CognitoCallback = {
    cognitoCallback: async (message, result, value, error) => {
      switch (result) {
        case LoginStatus.SUCCESS:
          this.router.navigateByUrl(this.redirectPath);
          break;

        default:
          break;
      }
    }
  };

  private createCredentials = (session: CognitoUserSession) => {
    AWS.config.credentials = this.cognitoUtil.buildCognitoCreds(session.getIdToken().getJwtToken());

    this.currentUserCredentials = AWS.config.credentials;

    // So, when CognitoIdentity authenticates a user, it doesn't actually hand us the IdentityID,
    // used by many of our other handlers. This is handled by some sly underhanded calls to AWS Cognito
    // API's by the SDK itself, automatically when the first AWS SDK request is made that requires our
    // security credentials. The identity is then injected directly into the credentials object.
    // If the first SDK call we make wants to use our IdentityID, we have a
    // chicken and egg problem on our hands. We resolve this problem by 'priming' the AWS SDK by calling a
    // very innocuous API call that forces this behavior.
    const clientParams: any = {};
    if (environment.stsEndpoint) {
      clientParams.endpoint = environment.stsEndpoint;
    }
    const sts = new STS(clientParams);

    const getCallerIdentity = new Promise(resolve => {
      sts.getCallerIdentity(function(err, data) {
        console.log('UserLoginService: Successfully set the AWS credentials');
        resolve();
      });
    });

    const getBackendUserData = this.backend.getUserData();

    return Promise.all([getCallerIdentity, getBackendUserData])
      .then(values => {
        localStorage.setItem(
          '_app',
          JSON.stringify({
            _userId: values[1]._id
          })
        );
      })
      .catch(err => {
        console.log(err);
      });
  };

  private onLoginSuccess = (callback: CognitoCallback, session: CognitoUserSession, cognitoUser: CognitoUser) => {
    this.createCredentials(session)
      .then(() => {
        this.loading = false;
        this.getUpdatedSession(session);
        callback.cognitoCallback(null, LoginStatus.SUCCESS, session);
      })
      .catch(err => {
        this.loading = false;
        const error = err.message || err;
        callback.cognitoCallback(error, LoginStatus.FAIL, null);
      });
  };

  public cognitoUserAuthCallback = (cognitoUser, callback) => {
    const mfaType = 'email';
    return {
      newPasswordRequired: (userAttributes, requiredAttributes) =>
        this.onChangePassword(userAttributes, requiredAttributes, cognitoUser, callback),
      onSuccess: result => this.onLoginSuccess(callback, result, cognitoUser),
      onFailure: err => this.onLoginError(callback, err, cognitoUser),
      mfaRequired: (challengeName, challengeParameters) => {
        callback.handleMFAStep(challengeName, challengeParameters, (confirmationCode: string) => {
          cognitoUser.sendMFACode(
            confirmationCode,
            {
              onSuccess: result => this.onLoginSuccess(callback, result, cognitoUser),
              onFailure: err => this.onLoginError(callback, err, cognitoUser)
            },
            mfaType
          );
        });
      }
    };
  };

  private onChangePassword = (
    userAttributes,
    requiredAttributes,
    cognitoUser: CognitoUser,
    callback: CognitoCallback
  ) => {
    this.validationRequired = {
      cognitoUser,
      userAttributes,
      requiredAttributes
    };
    this.loading = false;
    callback.cognitoCallback(`User needs to set password.`, LoginStatus.CHANGE_PASSWORD, null);
  };

  private onLoginError = (callback: CognitoCallback, err, cognitoUser: CognitoUser) => {
    this.loading = false;
    this.validationRequired = {
      cognitoUser
    };
    callback.cognitoCallback(err.message, LoginStatus.FAIL, null, err);
  };

  constructor(
    private store: Store<fromApp.IRootState>,
    public cognitoUtil: CognitoUtil,
    private router: Router,
    private backend: BackendEndpointService
  ) {

    this.store.dispatch(new appActions.SetCurrentUser(null));
  }

  authenticate(username: string, password: string) {
    console.log('UserLoginService: starting the authentication');

    const authenticationData = {
      Username: username,
      Password: password
    };
    const authenticationDetails = new AuthenticationDetails(authenticationData);

    const userData: {
      Username: string;
      Pool: any;
      Storage?: any;
    } = {
      Username: username,
      Pool: this.cognitoUtil.getUserPool()
    };

    console.log('UserLoginService: Params set...Authenticating the user');
    const cognitoUser = new CognitoUser(userData);
    console.log('UserLoginService: config is ' + AWS.config);

    this.loading = true;

    cognitoUser.authenticateUser(authenticationDetails, this.cognitoUserAuthCallback(cognitoUser, this.loginCallback));
  }

  authenticateUsingTokens(authResponse: { idToken; accessToken; refreshToken }, redirectPath) {
    this.redirectPath = redirectPath;
    return this.onLoginSuccess(
      this.redirectAfterLogin,
      new CognitoUserSession({
        IdToken: new CognitoIdToken({ IdToken: authResponse.idToken.jwtToken }),
        AccessToken: new CognitoAccessToken({ AccessToken: authResponse.accessToken.jwtToken }),
        RefreshToken: new CognitoRefreshToken({ RefreshToken: authResponse.refreshToken.token })
      }),
      null
    );
  }

  forgotPassword(username: string, callback: CognitoCallback) {
    const userData: {
      Username: string;
      Pool: any;
      Storage?: any;
    } = {
      Username: username,
      Pool: this.cognitoUtil.getUserPool()
    };

    const cognitoUser = new CognitoUser(userData);

    cognitoUser.forgotPassword({
      onSuccess: function() {},
      onFailure: function(err) {
        callback.cognitoCallback(err.message, null, null, err);
      },
      inputVerificationCode() {
        callback.cognitoCallback(null, null, null);
      }
    });
  }

  confirmNewPassword(email: string, verificationCode: string, password: string, callback: CognitoCallback) {
    const userData: {
      Username: string;
      Pool: any;
      Storage?: any;
    } = {
      Username: email,
      Pool: this.cognitoUtil.getUserPool()
    };

    const cognitoUser = new CognitoUser(userData);

    cognitoUser.confirmPassword(verificationCode, password, {
      onSuccess: function() {
        callback.cognitoCallback(null, null, null);
      },
      onFailure: function(err) {
        callback.cognitoCallback(err.message, null, null, err);
      }
    });
  }

  logout() {
    const currentUser = this.cognitoUtil.getCurrentUser();

    localStorage.removeItem('_app');

    if (currentUser !== null) {
      console.log('UserLoginService: Logging out');
      currentUser.signOut();
    }

    this.store.dispatch(new appActions.SetCurrentUser(null));
    clearInterval(this.sessionRefresher);
    this.sessionRefresher = null;
  }

  private startSessionRefresher() {
    this.sessionRefresher = setInterval(() => {
      this.getUpdatedSession();
    }, 1000 * 60 * 15); // refresh every 15 mins
  }

  private async getUpdatedSession(forceSession = null): Promise<any> {
    return new Promise(resolve => {
      // take the cognito user
      const cognitoUser = this.cognitoUtil.getCurrentUser();

      // take local mapping
      const ls = this.getLocalApp();

      if (cognitoUser != null && ls !== null && ls !== '' && forceSession && forceSession.isValid()) {
        this.currentUserSession = forceSession;
        this.store.dispatch(
          new appActions.SetCurrentUser({ ...this.currentUserSession.idToken.payload, userId: JSON.parse(ls)._userId })
        );
        resolve(forceSession);
      } else if (cognitoUser != null && ls !== null && ls !== '') {
        cognitoUser.getSession(async (err, session) => {
          if (err || !session.isValid()) {
            this.currentUserSession = null;
            this.store.dispatch(new appActions.SetCurrentUser(null));
            resolve(null);
          } else {
            this.currentUserSession = session;

            if (!this.currentUserCredentials) {
              AWS.config.credentials = this.cognitoUtil.buildCognitoCreds(session.getIdToken().getJwtToken());
              this.currentUserCredentials = AWS.config.credentials;
            } else {
              if (this.currentUserCredentials.needsRefresh()) {
                const refresh_token = session.getRefreshToken(); // you'll get session from calling cognitoUser.getSession()
                cognitoUser.refreshSession(refresh_token, async (error, newSession) => {
                  AWS.config.credentials = this.cognitoUtil.buildCognitoCreds(newSession.getIdToken().getJwtToken());
                  this.currentUserCredentials = AWS.config.credentials;
                  await this.cognitoUtil.refreshCredentials(this.currentUserCredentials);
                  this.currentUserSession = newSession;
                });
              }
            }

            this.store.dispatch(
              new appActions.SetCurrentUser({
                ...this.currentUserSession.idToken.payload,
                userId: JSON.parse(ls)._userId
              })
            );
            resolve(session);
          }
        });
      } else {
        this.currentUserSession = null;
        this.store.dispatch(new appActions.SetCurrentUser(null));
        resolve(null);
      }
    });
  }

  public async isAuthenticated(): Promise<boolean> {
    return Promise.resolve(true);
    // const sessionRefresher = this.sessionRefresher;

    // return new Promise<boolean>(async resolve => {
    //   if (sessionRefresher === null) {
    //     const session = await this.getUpdatedSession();

    //     if (session) {
    //       this.startSessionRefresher();
    //       resolve(true);
    //     } else {
    //       resolve(false);
    //     }
    //   } else {
    //     if (this.currentUserSession) {
    //       resolve(true);
    //     } else {
    //       resolve(false);
    //     }
    //   }
    // });
  }

  isAuthorized = (toState: any): Promise<boolean> => {

    return Promise.resolve(true);

    // return new Promise((resolve, reject) => {
    //   if (typeof toState.data === 'undefined' || typeof toState.data.groups === 'undefined') {
    //     reject();
    //   } else {
    //     const groups = toState.data.groups;
    //     if (groups.length === 0) {
    //       console.log(
    //         'routeSecurityEventHandler - ' + toState.url + ' does not need any authentication or authorization'
    //       );
    //       resolve();
    //     } else {
    //       this.cognitoUtil.getAuthData().then(
    //         authData => {
    //           const groupIntersection = authData.groups.filter(function(g) {
    //             return groups.indexOf(g) !== -1;
    //           });
    //           if (groupIntersection.length >= 1) {
    //             // The users in administrators group can acess to all apps
    //             if (groupIntersection.includes('administrators')) {
    //               console.log('routeSecurityEventHandler - Administrator found. Transition allowed');
    //               resolve();
    //             } else if (groupIntersection.includes('users')) {
    //               // The user is not in the administrators group, check the apps authorization
    //               console.log('routeSecurityEventHandler - User found. Check app authorization');
    //               // App type not defined on routes level
    //               if (typeof toState.data === 'undefined' || typeof toState.data.app === 'undefined') {
    //                 reject();
    //               } else {
    //                 if (this.isUserAppAuthorized(toState.data.app, authData.apps)) {
    //                   console.log('routeSecurityEventHandler - User authorized through authorization apps');
    //                   // App found in authorized apps
    //                   resolve();
    //                   return;
    //                 } else {
    //                   // User not authorized to access the app
    //                   reject();
    //                   return;
    //                 }
    //               }
    //             } else {
    //               // Unknown group
    //               reject();
    //               return;
    //             }
    //           } else {
    //             console.log('routeSecurityEventHandler - Current User is authenticated but NOT authorized');
    //             reject();
    //           }
    //         },
    //         err => {
    //           reject(err);
    //         }
    //       );
    //     }
    //   }
    // });
  };

  getLocalApp() {
    let ls = null;
    ls = localStorage.getItem('_app');

    return ls;
  }

  getUserIdMapping() {
    const _app = this.getLocalApp();

    if (_app !== null && _app !== '') {
      const userId = JSON.parse(_app)._userId;
      return userId;
    } else {
      console.log("UserLoginService: can't retrieve the current user");
      return null;
    }
  }

  isUserAppAuthorized(appValue: number, userAppsBitmask: number): boolean {
    return true;
    // return !!(userAppsBitmask & appValue);
  }

  getAppsAuthBitmaskList() {
    return [
      { name: 'Admin Application', value: 1 }, // 2^0
      { name: 'Planner Application', value: 2 }, // 2^1
      { name: 'Reporting Application', value: 4 }, // 2^2
      { name: 'Dashboard Application', value: 8 } // 2^3
    ];
  }

  getAppAuthValue(appName): number {
    const appAuth = this.getAppsAuthBitmaskList().filter(a => a.name === appName);

    if (appAuth.length === 0) {
      return 0;
    }
    return appAuth[0].value;
  }

  /*
   * Retrieve the password policy of the Cognito user pool.
   */
  getPasswordPolicy(): Promise<PasswordPolicy> {
    return new Promise((resolve, reject) => {
      this.backend.get(['/users/cognito/passwordPolicy']).then(
        passwordPolicy => {
          resolve(passwordPolicy);
        },
        err => {
          reject(err);
        }
      );
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/user-login.service.ts